import {
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  DATE_FORMAT_STR,
  formatDate,
  Column,
  getRawDate,
  Row,
  Typography,
  useLoading,
} from "best-common-react";
import React, { useEffect, useState } from "react";
import { getTicketRequestCharges, getTicketRequestRefunds } from "../../../api/RequesTixApi";
import { Finance } from "../../../types/Finance";
import { previousMonth, yesterday } from "../../../util/DateObjUtil";
import { ReportHandlerTypeProps } from "../ReportHandler";
import FinanceTable from "./FinanceTable";

const TicketRequestFinanceReport = ({ reportData, setReportData }: ReportHandlerTypeProps) => {
  const { setLoading } = useLoading();
  const { startDate, endDate } = reportData;
  const [dateRange, setDateRange] = useState<DateRange<Date>>({
    start: !!startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
    end: !!endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
  });
  const [charges, setCharges] = useState<Finance[]>([]);
  const [refunds, setRefunds] = useState<Finance[]>([]);

  const updateDate = ({ start, end }: DateRange<Date>) => {
    if (!!start && !!end) {
      setReportData({
        ...reportData,
        startDate: formatDate(start, DATE_FORMATTER_STR),
        endDate: formatDate(end, DATE_FORMATTER_STR),
      });
    }
  };

  const getChargeData = async (startDate: string, endDate: string) => {
    try {
      const result: Finance[] = await getTicketRequestCharges(startDate, endDate);
      setCharges(result);
    } catch (e) {
      console.error(e);
    }
  };

  const getRefundData = async (startDate: string, endDate: string) => {
    try {
      const result: Finance[] = await getTicketRequestRefunds(startDate, endDate);
      setRefunds(result);
    } catch (e) {
      console.error(e);
    }
  };

  const getData = async (startDate: Date, endDate: Date) => {
    try {
      if (!!startDate && !!endDate) {
        setLoading(true);
        await Promise.all([
          getChargeData(formatDate(startDate), formatDate(endDate)),
          getRefundData(formatDate(startDate), formatDate(endDate)),
        ]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newDate: DateRange<Date> = {
      start: startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
      end: endDate ? getRawDate(endDate, DATE_FORMAT_STR) : yesterday,
    };
    setDateRange(newDate);
    void getData(newDate.start!, newDate.end!);
  }, [startDate, endDate]);

  return (
    <>
      <Row>
        <Column width={4}>
          <DateRangeInput
            id="approval-date"
            label="Approval Date"
            value={dateRange}
            onChange={updateDate}
            maxRange={{
              months: 3,
            }}
            withPortal
            gutterBottom
            popoverPlacement="bottom-end"
          />
        </Column>
      </Row>
      <div>
        <Typography variant="headlineSmall">Charges</Typography>
        <FinanceTable data={charges} />
      </div>
      <div className="bcr-mt-2">
        <Typography variant="headlineSmall">Refunds</Typography>
        <FinanceTable data={refunds} />
      </div>
    </>
  );
};

export default TicketRequestFinanceReport;
